import { forwardRef, useEffect, useRef, useState } from "react";
import { Autocomplete, Chip, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ConnectedBookingData } from "../../../features/Booking-Form/typings/connected-bookingData";
import { ColleagueFilterState } from "./types/GroupFilters.type";
import { calculateTagLimit } from "./functions/calculateTagLimit.function";

type P = {
  connectedBookingData: ConnectedBookingData[] | undefined;
  selectedColleagueFilter: ConnectedBookingData[];
  setSelectedColleagueFilter: (filter: ConnectedBookingData[]) => void;
  onClose?: () => void;
  colleagueFilterState: ColleagueFilterState;
};

export const ColleagueBookingFilters: React.FC<P> = forwardRef<HTMLDivElement, P>((props, ref) => {
  const {
    connectedBookingData,
    selectedColleagueFilter,
    setSelectedColleagueFilter,
    colleagueFilterState
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [tagLimit, setTagLimit] = useState(2);

  const { t } = useTranslation();

  const [options, setOptions] = useState<ConnectedBookingData[]>([]);

  const handleColleaguesChange = (event: React.SyntheticEvent, value: ConnectedBookingData[]) => {
    setSelectedColleagueFilter(value);
  };

  useEffect(() => {
    // initially select all the booking data items
    setSelectedColleagueFilter(connectedBookingData ?? []);
  }, []);

  useEffect(() => {
    if (!connectedBookingData) return setOptions([]);
    setOptions(connectedBookingData);
  }, [connectedBookingData]);

  useEffect(() => {
    // when the colleagues booking filter toggled to be empty(second click)
    if (colleagueFilterState === ColleagueFilterState.EMPTY) {
      setSelectedColleagueFilter([]);
    }
  }, [colleagueFilterState]);

  // Observe Autocomplete container width changes
  useEffect(() => {
    if (
      selectedColleagueFilter.length > 0 &&
      colleagueFilterState !== ColleagueFilterState.DEACTIVATED
    ) {
      const resizeObserver = new ResizeObserver(() => {
        // 30 padding + extra 34 padding so the content stays in one line only
        calculateTagLimit(containerRef, 64, selectedColleagueFilter, setTagLimit);
      });

      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }

      // Initial calculation
      calculateTagLimit(containerRef, 64, selectedColleagueFilter, setTagLimit);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [selectedColleagueFilter, colleagueFilterState]);

  return (
    <div ref={containerRef}>
      <Autocomplete
        ref={ref}
        data-testid="find-colleagues-autocomplete"
        multiple
        disableCloseOnSelect
        id="tags-outlined8"
        options={options}
        disableClearable
        value={selectedColleagueFilter}
        onChange={handleColleaguesChange}
        renderOption={(props, option) => <BookingMemeberOptionList props={props} option={option} />}
        noOptionsText={t("No Options")}
        renderInput={params => (
          <TextField
            {...params}
            fullWidth
            data-testid={"find-colleagues-field"}
            placeholder={t("Find your colleagues")}
          />
        )}
        limitTags={tagLimit}
        renderTags={(tagValue, getTagProps) => {
          return tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.userId}
              data-testid={`${option.userId}-select-chip`}
              label={option.firstName + " " + option.lastName}
            />
          ));
        }}
        getOptionLabel={option => option.firstName + " " + option.lastName}
        isOptionEqualToValue={(option, value) => option.userId === value.userId}
      />
    </div>
  );
});

export function BookingMemeberOptionList({
  props,
  option
}: Readonly<{
  props: React.HTMLAttributes<HTMLLIElement>;
  option: ConnectedBookingData;
}>) {
  return (
    <li {...props} key={option.userId}>
      <div data-testid="team-selection-option">{option.firstName + " " + option.lastName}</div>
    </li>
  );
}
